import {ReactNode, useEffect, useState} from 'react';
import {faCircleChevronDown} from 'assets/icons/fontawesome/duotone';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useParams} from 'react-router-dom';
import styles from './Accordeon.module.scss';

export interface AccordeonItem {
  id: number;
  titleRight?: ReactNode;
  content: ReactNode;
  variant: 'positive' | 'attention' | 'negative' | 'empty';
  title?: string;
  subtitle?: string;
  score: number | string;
  icon: IconProp;
  onClick?(isOpen: boolean): void;
  progressLimit: number;
}

export interface AccordeonProps {
  items: AccordeonItem[];
}

function Accordeon({items}: AccordeonProps): JSX.Element {
  const [selectedItemIndex, setSelectedItemIndex] =
    useState<number | null>(null);

  const {composition_id} = useParams();

  const {trackEvent} = useTrackEvent();

  // Always start with the first item (the smaller) as the selected one
  useEffect(() => {
    if (items[0]?.onClick) {
      handleItemClick(0, items[0].onClick);
    }
  }, [items.length]);

  function handleItemClick(
    itemIndex: number,
    callback?: (isOpen: boolean) => void,
  ) {
    if (selectedItemIndex === itemIndex) {
      setSelectedItemIndex(null);
      callback?.(false);
    } else {
      // Only track the event when user actually click the competence, rather than always on mount (line 38)
      if (selectedItemIndex !== null) {
        trackEvent('step3_feedback_competence_expanded', {
          composition: {
            composition_id: Number(composition_id),
          },
          competence: {
            competence_id: items[itemIndex].id,
          },
        });
      }

      setSelectedItemIndex(itemIndex);
      callback?.(true);
    }
  }

  return (
    <div className={styles.list}>
      {items.map((item, index) => {
        const isOpen = index === selectedItemIndex;

        return (
          <div
            key={item.id}
            className={`
              ${styles.wrapper} 
              ${isOpen ? styles.open : ''} 
              ${styles[item.variant]}
            `}
            data-focusid={`competence${index}`}
          >
            <h3 className={`${styles.title} ${isOpen ? styles.open : ''}`}>
              <button
                type="button"
                id={`button_${item.id}`}
                aria-expanded={isOpen}
                aria-controls={`panel_${item.id}`}
                className={styles.accordeonButton}
                disabled={item.variant === 'empty'}
                onClick={() => handleItemClick(index, item.onClick)}
              >
                <div className={styles.titleContent}>
                  <div className={styles.main}>
                    <div className={styles.left}>
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={styles.icon}
                      />

                      <div className={styles.headerText}>
                        {item.variant !== 'empty' && <h4>{item.title}</h4>}

                        <p>
                          {item.variant === 'empty' ? (
                            <span>Carregando...</span>
                          ) : (
                            <div id={item.subtitle}>
                              {item.subtitle} - <span>{item.score} pontos</span>
                            </div>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className={styles.right}>
                      <div className={item.titleRight ? styles.hasTitle : ''}>
                        <b>{item.titleRight}</b>
                      </div>

                      <FontAwesomeIcon
                        icon={faCircleChevronDown}
                        color="#2A5082"
                        size="xl"
                      />
                    </div>
                  </div>

                  <progress
                    aria-label={`Progresso: ${item.score} de ${item.progressLimit} pontos`}
                    value={item.score}
                    max={item.progressLimit}
                  />
                </div>
              </button>
            </h3>

            <div
              role="region"
              id={`panel_${item.id}`}
              aria-labelledby={`button_${item.id}`}
              className={styles.content}
            >
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Accordeon;
